import { useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getPartnerHeaders } from "@/integrations/php/client";
import { LeadStageType } from "@/context/LeadContext";
import { PartnerLead, stageMeta } from "@/types/partnerPortal";
import { toast } from "@/components/ui/use-toast";

const queryKey = (partnerId?: string) => ["partner-leads", partnerId] as const;

const fetchPartnerLeads = async (partnerId: string): Promise<PartnerLead[]> => {
  const response = await fetch(
    `/api/get-partner-leads.php?partner_id=${encodeURIComponent(partnerId)}`,
    {
      credentials: "include",
      headers: getPartnerHeaders(),
    },
  );

  const body = await response.json().catch(() => null);
  if (!response.ok || !Array.isArray(body)) {
    throw new Error("Δεν ήταν δυνατή η φόρτωση των leads.");
  }

  return body.map((lead) => ({
    id: String(lead.id),
    name: String(lead.name || "Χωρίς όνομα"),
    phone: String(lead.phone || ""),
    email: lead.email ? String(lead.email) : undefined,
    service: lead.service,
    timeframe: String(lead.timeframe || ""),
    stage: (lead.stage || "Νέο") as LeadStageType,
    comments: lead.comments ? String(lead.comments) : undefined,
    createdAt: String(lead.created_at),
    updatedAt: lead.updated_at ? String(lead.updated_at) : undefined,
    smsSent: Boolean(lead.sms_sent),
    linkOpened: Boolean(lead.link_opened),
  }));
};

export const usePartnerLeads = (partnerId?: string) => {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: queryKey(partnerId),
    queryFn: () => fetchPartnerLeads(partnerId as string),
    enabled: Boolean(partnerId),
    staleTime: 45_000,
    refetchOnWindowFocus: true,
  });

  const stageMutation = useMutation({
    mutationFn: async ({ leadId, stage }: { leadId: string; stage: LeadStageType }) => {
      const response = await fetch("/api/update-lead-stage.php", {
        method: "POST",
        credentials: "include",
        headers: getPartnerHeaders(),
        body: JSON.stringify({ lead_id: leadId, stage, partner_id: partnerId }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        const message = typeof body?.error === "string"
          ? body.error
          : body?.error?.message;
        throw new Error(message || "Η αλλαγή σταδίου απέτυχε.");
      }
      return { leadId, stage };
    },
    onMutate: async ({ leadId, stage }) => {
      await queryClient.cancelQueries({ queryKey: queryKey(partnerId) });
      const previous = queryClient.getQueryData<PartnerLead[]>(queryKey(partnerId));
      queryClient.setQueryData<PartnerLead[]>(queryKey(partnerId), (current = []) =>
        current.map((lead) => (lead.id === leadId ? { ...lead, stage } : lead)),
      );
      return { previous };
    },
    onError: (error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey(partnerId), context.previous);
      }
      toast({
        title: "Δεν αποθηκεύτηκε η αλλαγή",
        description: error instanceof Error ? error.message : "Δοκιμάστε ξανά.",
        variant: "destructive",
      });
    },
    onSuccess: ({ stage }) => {
      toast({
        title: "Το στάδιο ενημερώθηκε",
        description: `Μεταφέρθηκε στα «${stageMeta(stage).label}».`,
      });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKey(partnerId) });
    },
  });

  const markViewed = useCallback((leadId: string) => {
    queryClient.setQueryData<PartnerLead[]>(queryKey(partnerId), (current = []) =>
      current.map((lead) => (lead.id === leadId ? { ...lead, linkOpened: true } : lead)),
    );
  }, [partnerId, queryClient]);

  return {
    leads: query.data ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error instanceof Error ? query.error.message : null,
    refetch: query.refetch,
    updateStage: (leadId: string, stage: LeadStageType) =>
      stageMutation.mutate({ leadId, stage }),
    updatingLeadId: stageMutation.isPending
      ? stageMutation.variables?.leadId ?? null
      : null,
    markViewed,
  };
};
